import { useEffect } from 'react';
import Lenis from 'lenis';

/**
 * useLenis — Dom-Pérignon-style buttery scroll.
 * Hijacks native scroll and applies inertial physics with a soft lerp so the
 * whole site feels heavy, luxurious and meditative. Runs a single Lenis
 * instance for the entire app; no-op if the user has requested reduced motion
 * or is on a touch device (native inertia there is already excellent and
 * fighting it adjacent to iOS bounce breaks the address-bar chrome).
 */
export function useLenis({ enabled = true, lerp = 0.08 } = {}) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    if (prefersReducedMotion || isCoarsePointer) return;

    const lenis = new Lenis({
      lerp,                 // 0.08 = "buttery" per Dom Pérignon reference doc
      wheelMultiplier: 1,
      touchMultiplier: 1.2,
      smoothWheel: true,
      autoRaf: true,        // Lenis manages its own requestAnimationFrame loop
    });

    // Expose so hash-scroll & CTA smooth-scroll code can defer to Lenis.
    window.__lenis = lenis;

    // Bridge for consumers that listen to 'scroll' — Lenis emits its own event
    // and forwards it to the window so IntersectionObservers etc keep firing.
    lenis.on('scroll', () => {
      window.dispatchEvent(new Event('lenis:scroll'));
    });

    return () => {
      lenis.destroy();
      if (window.__lenis === lenis) delete window.__lenis;
    };
  }, [enabled, lerp]);
}
